import { useMemo } from "react";
import type {
  AnnualLeaveResponse,
  UnpaidLeaveResponse,
  MedicalLeaveResponse,
} from "@/features/hr/leave-applications/types";
import type { WorkerListItem } from "@/features/hr/employees";
import { getActiveLeavesForWorker } from "@/features/hr/leave-applications/utils";
import { useGetMeQuery } from "@/features/auth/api";
import { useGetSelfProfileQuery } from "../../profile/api";
import { useGetSelfRequestsQuery } from "../api";
import {
  transformSelfRequestsToAnnualLeaves,
  transformSelfRequestsToUnpaidLeaves,
  transformSelfRequestsToMedicalLeaves,
} from "../utils/leaveTransform";
import { isActiveRequest } from "../utils/requestRestrictions";
import type { SelfServiceProfile, WorkerProfile, ManagerProfile } from "../../shared/types";

export interface UseSelfLeaveRequestsResult {
  profile: SelfServiceProfile | undefined;
  workerProfile: WorkerProfile | null;
  employeeLike: WorkerListItem | null;
  vacationDaysAvailable: number | undefined;
  annualLeaves: AnnualLeaveResponse[];
  unpaidLeaves: UnpaidLeaveResponse[];
  medicalLeaves: MedicalLeaveResponse[];
  activeLeaves: ReturnType<typeof getActiveLeavesForWorker>;
  isProfileLoading: boolean;
  isProfileError: boolean;
}

function buildWorkerProfileFromManager(mgr: ManagerProfile): WorkerProfile {
  return {
    user_type: "worker" as const,
    id: mgr.id,
    full_name: mgr.full_name,
    surname: mgr.surname,
    name: mgr.name,
    father_name: mgr.father_name,
    iin: mgr.iin,
    email: mgr.email,
    phone: mgr.phone,
    job_position_kk: mgr.job_position_kk,
    job_position_ru: mgr.job_position_ru,
    start_date: mgr.start_date ?? null,
    contract_type: mgr.contract_type ?? null,
    status: mgr.status ?? "active",
    active_contract: mgr.active_contract ?? null,
    vacation_days: mgr.vacation_days,
    documents: mgr.documents,
    id_documents: mgr.id_documents,
  };
}

function buildEmployeeLike(
  workerProfile: WorkerProfile | null,
  vacationDaysAvailable: number | undefined,
): WorkerListItem | null {
  if (!workerProfile) return null;
  return {
    id: workerProfile.id,
    user_id: workerProfile.id,
    iin: workerProfile.iin,
    full_name: workerProfile.full_name,
    date_of_birth: "",
    gender: "other",
    status: workerProfile.status,
    contacts: null,
    position: workerProfile.job_position_ru ?? null,
    active_contract: workerProfile.active_contract
      ? {
          id: 0,
          contract_number: "",
          job_position_ru: workerProfile.job_position_ru,
          job_position_kk: workerProfile.job_position_kk,
          work_position: null,
          salary_amount: workerProfile.active_contract.salary?.toString() ?? "",
          start_date: workerProfile.active_contract.start_date,
        }
      : null,
    vacation_days_available: vacationDaysAvailable ?? 0,
    roles: [],
    created_at: workerProfile.start_date || "",
    trial_period_end_date: null,
    social_categories: [],
  };
}

interface UseSelfLeaveRequestsOptions {
  skip?: boolean;
}

export function useSelfLeaveRequests(options: UseSelfLeaveRequestsOptions = {}): UseSelfLeaveRequestsResult {
  const skip = options.skip ?? false;
  const { data: profile, isLoading: isProfileLoading, isError: isProfileError } = useGetSelfProfileQuery(undefined, { skip });
  const { data: meData } = useGetMeQuery(undefined, { skip });
  const { data: annualRequests } = useGetSelfRequestsQuery({ type: "annual_leave" }, { skip });
  const { data: unpaidRequests } = useGetSelfRequestsQuery({ type: "unpaid_leave" }, { skip });
  const { data: medicalRequests } = useGetSelfRequestsQuery({ type: "medical_leave" }, { skip });

  const workerProfile = useMemo(function deriveWorkerProfile() {
    if (profile?.user_type === "worker") return profile as WorkerProfile;
    if (profile?.user_type === "manager" && meData?.has_worker_profile === true) {
      return buildWorkerProfileFromManager(profile as ManagerProfile);
    }
    return null;
  }, [profile, meData]);

  const vacationDaysAvailable = useMemo(function deriveVacationDays() {
    const raw = workerProfile?.vacation_days ?? meData?.profile?.vacation_days;
    if (raw == null || raw === "") return undefined;
    const num = parseFloat(String(raw));
    return Number.isNaN(num) ? undefined : num;
  }, [workerProfile?.vacation_days, meData?.profile?.vacation_days]);

  const employeeLike = useMemo(
    () => buildEmployeeLike(workerProfile, vacationDaysAvailable),
    [workerProfile, vacationDaysAvailable],
  );

  const annualLeaves = useMemo(() => {
    if (!annualRequests || !workerProfile) return [];
    return transformSelfRequestsToAnnualLeaves(annualRequests.filter(isActiveRequest), workerProfile);
  }, [annualRequests, workerProfile]);

  const unpaidLeaves = useMemo(() => {
    if (!unpaidRequests || !workerProfile) return [];
    return transformSelfRequestsToUnpaidLeaves(unpaidRequests.filter(isActiveRequest), workerProfile);
  }, [unpaidRequests, workerProfile]);

  const medicalLeaves = useMemo(() => {
    if (!medicalRequests || !workerProfile) return [];
    return transformSelfRequestsToMedicalLeaves(medicalRequests.filter(isActiveRequest), workerProfile);
  }, [medicalRequests, workerProfile]);

  const activeLeaves = useMemo(() => {
    if (!employeeLike) return [];
    return getActiveLeavesForWorker(employeeLike.id, annualLeaves, unpaidLeaves, medicalLeaves);
  }, [employeeLike, annualLeaves, unpaidLeaves, medicalLeaves]);

  return {
    profile,
    workerProfile,
    employeeLike,
    vacationDaysAvailable,
    annualLeaves,
    unpaidLeaves,
    medicalLeaves,
    activeLeaves,
    isProfileLoading,
    isProfileError,
  };
}
